<template>
  <!--
    Renders only when the backend gave us a snapshot AND the assistant is on for
    this school. `aiUsage` is null for non-admins and on a backend without the
    field, so most staff see nothing here at all — by design.
  -->
  <router-link v-if="show" data-testid="ai-usage-widget"
    :to="{ name: 'admin-settings', query: { tab: 'ai_assistant' } }"
    class="block bg-white rounded-2xl p-6 shadow-sm border border-gray-100 hover:shadow-md transition">
    <div class="flex justify-between items-start">
      <div>
        <p class="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">AI Usage</p>
        <h3 class="text-2xl font-bold text-gray-900" data-testid="ai-usage-widget-figure">{{ monthlySpendLabel }}</h3>
      </div>
      <div class="w-10 h-10 rounded-lg bg-indigo-50 flex items-center justify-center text-indigo-600">
        <i class="pi pi-sparkles text-xl"></i>
      </div>
    </div>

    <!-- Capped: a bar against the ceiling. Uncapped: nothing to draw against. -->
    <template v-if="!isUncapped">
      <div class="w-full bg-gray-100 rounded-full h-1.5 mt-4">
        <div class="h-1.5 rounded-full" :class="barClass" :style="{ width: `${monthlyPercent}%` }"
          data-testid="ai-usage-widget-bar"></div>
      </div>
      <p class="text-xs text-gray-500 mt-2" data-testid="ai-usage-widget-caption">
        <span class="font-bold text-gray-700">{{ monthlyRemainingLabel }}</span> of
        {{ monthlyCapLabel }} left this period
      </p>
    </template>
    <p v-else class="text-xs text-gray-500 mt-4" data-testid="ai-usage-widget-caption">
      Spent this period — no spending limit set.
    </p>

    <p v-if="capReached" class="text-xs text-gray-700 mt-2" data-testid="ai-usage-widget-cap-reached">
      <i class="pi pi-pause-circle text-[11px]"></i>
      Assistant paused until the next period — see settings.
    </p>
    <p v-else-if="isApproachingCap" class="text-xs text-amber-700 mt-2" data-testid="ai-usage-widget-warning">
      <i class="pi pi-info-circle text-[11px]"></i>
      {{ monthlyPercent }}% of this period's limit used.
    </p>
  </router-link>
</template>

<script setup lang="ts">
import { useSchoolAiUsage } from '@/composables/useSchoolAiUsage';
import { useAiAssistantAccess } from '@/composables/useAiAssistantAccess';

/**
 * Compact AI-spend card for the staff dashboard (Paul asked for the figure to
 * live on the dashboard, not only in Settings). Deliberately minimal — a
 * figure, a bar and a link into Settings -> AI Assistant, which holds the full
 * breakdown. Follows the PulseRow card shell so it sits in the existing grid.
 *
 * READ-ONLY, like the Settings panel: a school cannot change its own cap.
 */
const {
  hasUsage,
  isUncapped,
  capReached,
  isApproachingCap,
  monthlyPercent,
  monthlySpendLabel,
  monthlyCapLabel,
  monthlyRemainingLabel,
  barClass,
} = useSchoolAiUsage();

// The dashboard is shared by every staff member; showing an AI cost card to a
// school that has the assistant switched off would just be clutter.
const { showAdminEntry } = useAiAssistantAccess();

const show = computed(() => hasUsage.value && showAdminEntry.value);
</script>
